/**
 * Dashboard Propriétaire Optimisé
 * Phase 3.2 - Architecture refondée avec réutilisation maximale
 */

import React, { useState } from 'react';
import {
  Container,
  Box,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';

// Hooks optimisés
import { useOwnerDashboard, usePeriodSelector } from '../../hooks/useDashboardUnified';

// Composants de base réutilisables
import {
  DashboardHeader,
  MetricsGrid,
  DashboardWidget
} from './DashboardBase';

// Widgets spécialisés
import OwnerOverviewWidget from './widgets/OwnerOverviewWidget';
import OwnerRevenueWidget from './widgets/OwnerRevenueWidget';
import OwnerReservationsWidget from './widgets/OwnerReservationsWidget';
import OwnerAvailabilityWidget from './widgets/OwnerAvailabilityWidget';
import OwnerQuickActionsWidget from './widgets/OwnerQuickActionsWidget';

// Constants
import {
  STATS_PERIODS,
  PERIOD_LABELS,
  OWNER_METRICS,
  OWNER_METRIC_LABELS,
  METRIC_ICONS
} from '../../constants/dashboardConstants';

const OwnerDashboardOptimized = () => {
  // Gestion des périodes avec hook dédié
  const { period, customStart, customEnd, updatePeriod } = usePeriodSelector();
  
  // Dashboard principal avec auto-refresh
  const { 
    dashboard, 
    loading, 
    error, 
    refresh,
    lastUpdated 
  } = useOwnerDashboard({
    period,
    customStart,
    customEnd,
    autoRefresh: true,
    refreshInterval: 300000 // 5 minutes
  });

  console.log('🔥 DASHBOARD COMPONENT - dashboard:', dashboard, 'loading:', loading, 'error:', error);

  // État local pour l'UI
  const [showSettings, setShowSettings] = useState(false);

  // Configuration des métriques pour la grille
  const metricsConfig = {
    [OWNER_METRICS.TOTAL_REVENUE]: {
      label: OWNER_METRIC_LABELS[OWNER_METRICS.TOTAL_REVENUE],
      icon: METRIC_ICONS[OWNER_METRICS.TOTAL_REVENUE],
      format: 'currency',
      color: 'success'
    },
    [OWNER_METRICS.PENDING_REVENUE]: {
      label: OWNER_METRIC_LABELS[OWNER_METRICS.PENDING_REVENUE],
      icon: METRIC_ICONS[OWNER_METRICS.PENDING_REVENUE],
      format: 'currency',
      color: 'warning'
    },
    [OWNER_METRICS.ACTIVE_RESERVATIONS]: {
      label: OWNER_METRIC_LABELS[OWNER_METRICS.ACTIVE_RESERVATIONS],
      icon: METRIC_ICONS[OWNER_METRICS.ACTIVE_RESERVATIONS],
      format: 'number',
      color: 'primary'
    },
    [OWNER_METRICS.OCCUPANCY_RATE]: {
      label: OWNER_METRIC_LABELS[OWNER_METRICS.OCCUPANCY_RATE],
      icon: METRIC_ICONS[OWNER_METRICS.OCCUPANCY_RATE],
      format: 'percentage',
      color: 'info'
    }
  };

  // Handlers
  const handlePeriodChange = (event) => {
    updatePeriod(event.target.value);
  };

  const handleExportData = () => {
    // TODO: Implémenter l'export des données
    console.log('Export data for period:', period);
  };

  const handleOpenCalendar = () => {
    // TODO: Naviguer vers le calendrier unifié
    console.log('Open unified calendar');
  };

  // Actions pour le header
  const headerActions = (
    <>
      {/* Sélecteur de période */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Période</InputLabel>
        <Select
          value={period}
          label="Période"
          onChange={handlePeriodChange}
        >
          {Object.entries(PERIOD_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Actions rapides */}
      <Tooltip title="Calendrier unifié">
        <IconButton onClick={handleOpenCalendar} color="primary">
          <CalendarIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Exporter les données">
        <IconButton onClick={handleExportData} color="secondary">
          <DownloadIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Paramètres du dashboard">
        <IconButton onClick={() => setShowSettings(!showSettings)}>
          <SettingsIcon />
        </IconButton>
      </Tooltip>

      <Button
        variant="outlined"
        startIcon={<RefreshIcon />}
        onClick={refresh}
        disabled={loading}
        size="small"
      >
        Actualiser
      </Button>
    </>
  );

  // Gestion des erreurs
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header du dashboard */}
      <DashboardHeader
        title="Dashboard Propriétaire"
        subtitle={`Vue d'ensemble de vos activités - ${PERIOD_LABELS[period]}`}
        actions={headerActions}
      />

      {/* Grille de métriques principales */}
      <Box mb={4}>
        <MetricsGrid
          metrics={dashboard?.metrics || {}}
          metricConfig={metricsConfig}
          loading={loading}
          onRefresh={refresh}
          gridCols={{ xs: 1, sm: 2, md: 4 }}
        />
      </Box>

      {/* Widgets principaux */}
      <Grid container spacing={3}>
        {/* Vue d'ensemble détaillée */}
        <Grid item xs={12} lg={8}>
          <OwnerOverviewWidget
            data={dashboard?.widgets?.overview}
            loading={loading}
            period={period}
            onRefresh={refresh}
          />
        </Grid>

        {/* Actions rapides */}
        <Grid item xs={12} lg={4}>
          <OwnerQuickActionsWidget
            actions={dashboard?.widgets?.quick_actions}
            loading={loading}
          />
        </Grid>

        {/* Revenus */}
        <Grid item xs={12} md={6}>
          <OwnerRevenueWidget
            data={dashboard?.widgets?.revenue}
            loading={loading}
            period={period}
            onRefresh={refresh}
          />
        </Grid>

        {/* Réservations */}
        <Grid item xs={12} md={6}>
          <OwnerReservationsWidget
            data={dashboard?.widgets?.reservations}
            loading={loading}
            period={period}
            onRefresh={refresh}
          />
        </Grid>

        {/* Disponibilités */}
        <Grid item xs={12}>
          <OwnerAvailabilityWidget
            data={dashboard?.widgets?.availability}
            loading={loading}
            period={period}
            onRefresh={refresh}
          />
        </Grid>
      </Grid>

      {/* Informations de mise à jour */}
      {lastUpdated && !loading && (
        <Box mt={3} textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Dernière mise à jour : {lastUpdated.toLocaleTimeString('fr-FR')}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default OwnerDashboardOptimized;
